import { Component, DoCheck, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { Invoice } from '../../models/invoice/invoice';
import { PaymentAccount } from '../../models/account/payment-account';
import {
  AccountCodingHistoryEntry,
  AccountCodingCopyPayload,
  AccountCodingSuggestionDetail,
  AccountCodingSuggestionResponse,
  CodingSuggestionApiResponse,
  RecordCodingFeedbackRequest,
} from '../../models/invoice/account-coding-suggestion';
import { InvoiceService } from 'src/app/data/service/invoice.service';
import { take } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { ToasterMessageService } from 'src/app/shared/toaster-message/toaster-message.service';
import { ToasterTypes } from 'src/app/core/enum/toaster-type.enum';

@Component({
  selector: 'app-coding-suggestions',
  templateUrl: './coding-suggestions.component.html',
  styleUrls: ['./coding-suggestions.component.scss'],
})
export class CodingSuggestionsComponent implements OnChanges, DoCheck {
  @Input() invoice: Invoice;
  @Input() accounts: PaymentAccount[] = [];
  @Input() label: string = 'Suggestions';
  @Input() disabled: boolean = false;

  @Output() suggestionApplied = new EventEmitter<PaymentAccount>();
  @Output() codingCopied = new EventEmitter<AccountCodingCopyPayload>();

  showPanel = false;
  codingSuggestionsLoading = false;
  codingSuggestionsError = '';
  codingSuggestions: AccountCodingSuggestionResponse | null = null;
  expandedHistoryIds = new Set<string>();
  expandedRecentInvoiceIds = new Set<string>();
  historyDetails: AccountHistoryDetail[] = [];
  recentInvoices: RecentInvoiceCard[] = [];
  private lastSupplierId: string | null = null;
  selectedRecentKey: string | null = null;

  feedbackOpenId: string | null = null;
  feedbackNote = '';
  feedbackSubmitting = false;
  expandedOtherKeys = new Set<string>();

  constructor(
    private invoiceService: InvoiceService,
    private toaster: ToasterMessageService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['invoice']) {
      const currentSupplierId = this.invoice?.supplierId || null;
      const isSupplierChanged =
        !changes['invoice'].firstChange && currentSupplierId !== this.lastSupplierId;

      this.lastSupplierId = currentSupplierId;

      if (!changes['invoice'].firstChange) {
        this.resetLocalState();
      }

      if (isSupplierChanged && this.showPanel) {
        this.fetchCodingSuggestions();
      }
    }
  }

  ngDoCheck(): void {
    const currentSupplierId = this.invoice?.supplierId || null;
    if (currentSupplierId !== this.lastSupplierId) {
      this.lastSupplierId = currentSupplierId;
      this.resetLocalState();
      if (this.showPanel) {
        this.fetchCodingSuggestions();
      }
    }
  }

  toggleCodingSuggestions(event?: Event) {
    event?.stopPropagation();
    if (this.disabled) {
      return;
    }

    if (this.showPanel) {
      this.showPanel = false;
      return;
    }

    this.showPanel = true;
    this.codingSuggestionsError = '';
    if (!this.codingSuggestions && !this.codingSuggestionsLoading) {
      this.fetchCodingSuggestions();
    }
  }

  closeCodingSuggestionsPanel(event?: Event) {
    event?.stopPropagation();
    this.showPanel = false;
  }

  reloadCodingSuggestions(event?: Event) {
    event?.stopPropagation();
    this.codingSuggestions = null;
    this.fetchCodingSuggestions();
  }

  private fetchCodingSuggestions() {
    const invoiceId = this.invoice?.id?.toString();
    // App stores the active org under localStorage.SelectedOrganization
    // everywhere except a few legacy spots that also write sessionStorage.organizationId.
    // Honour both so deep links / fresh tabs (no session yet) still work.
    const organizationId =
      sessionStorage.organizationId
      || localStorage.getItem('SelectedOrganization')
      || localStorage.getItem('organizationId')
      || '';

    if (!invoiceId) {
      this.codingSuggestionsError = 'No invoice loaded — coding suggestions require an open invoice.';
      this.codingSuggestionsLoading = false;
      return;
    }
    if (!organizationId) {
      this.codingSuggestionsError = 'Select an organisation to fetch coding suggestions.';
      this.codingSuggestionsLoading = false;
      return;
    }

    this.codingSuggestionsLoading = true;
    this.codingSuggestionsError = '';

    this.invoiceService
      .getCodingSuggestions(organizationId, [invoiceId])
      .pipe(take(1))
      .subscribe(
        ([response]: [CodingSuggestionApiResponse]) => {
          this.codingSuggestions = this.mapCodingSuggestionsFromNewApi(response);
          this.recentInvoices = [];
          this.historyDetails = [];
          this.codingSuggestionsLoading = false;
        },
        (error: HttpErrorResponse) => {
          if (error?.status === 404) {
            this.codingSuggestions = { contactHistory: [], aiSuggestion: null };
            this.codingSuggestionsError = '';
          } else if (error?.status === 0) {
            this.codingSuggestionsError =
              'Unable to reach the server. Check your connection and try again.';
          } else {
            this.codingSuggestionsError =
              (error?.error as { message?: string })?.message ||
              error?.message ||
              'Unable to load coding suggestions right now.';
          }
          this.codingSuggestionsLoading = false;
        }
      );
  }

  private mapCodingSuggestionsFromNewApi(response: CodingSuggestionApiResponse): AccountCodingSuggestionResponse {
    if (!response) {
      return { contactHistory: [], aiSuggestion: null, allSuggestions: [], suggestedAccountCode: [] };
    }

    const sorted = [...response.suggestedAccountCode].sort((a, b) => (b.confidence ?? 0) - (a.confidence ?? 0));
    const allSuggestions = sorted.map((s, idx) => ({
      accountId: s.accountId,
      accountName: s.accountName,
      accountCode: s.accountCode,
      confidence: s.confidence,
      reasoning: s.reason,
      source: s.source || s.type,
      suggestionId: s.id != null ? String(s.id) : `sug-${idx}`,
    }));

    return {
      contactHistory: [],
      aiSuggestion: allSuggestions[0] ?? null,
      allSuggestions,
    };
  }

  /** Resolve a suggestion to a concrete accountId from the local chart of accounts.
   *  Falls back to the suggestion's own accountId when present. Returns '' if
   *  nothing can be matched (e.g. "Unknown account" suggestions with no fields). */
  private resolveSuggestionAccountId(suggestion: AccountCodingSuggestionDetail): string {
    if (suggestion.accountId) return suggestion.accountId;
    const matched = this.accounts?.find((acc) => {
      const code = suggestion.accountCode?.toLowerCase?.();
      const name = suggestion.accountName?.toLowerCase?.();
      return (
        (!!code && acc.code?.toLowerCase?.() === code) ||
        (!!name && acc.name?.toLowerCase?.() === name)
      );
    });
    return matched?.id || matched?.accountID || '';
  }

  applySpecificSuggestion(suggestion: { accountId?: string; accountName?: string; accountCode?: string }) {
    const matched = this.accounts?.find((acc) => {
      const suggestionId = suggestion.accountId?.toLowerCase?.();
      const suggestionCode = suggestion.accountCode?.toLowerCase?.();
      const suggestionName = suggestion.accountName?.toLowerCase?.();
      return (
        (!!suggestionId && (acc.id?.toLowerCase?.() === suggestionId || acc.accountID?.toLowerCase?.() === suggestionId)) ||
        (!!suggestionCode && acc.code?.toLowerCase?.() === suggestionCode) ||
        (!!suggestionName && acc.name?.toLowerCase?.() === suggestionName)
      );
    });

    if (!matched) {
      this.toaster.displayToasterState(ToasterTypes.Warning, '', 'Suggested account is not available in your chart of accounts.');
      return;
    }

    this.suggestionApplied.emit(matched);
    this.closeCodingSuggestionsPanel();
    this.toaster.displayToasterState(ToasterTypes.Success, '', 'Account updated with coding suggestion.');
  }

  toggleHistoryDetails(entry: AccountCodingHistoryEntry, event?: Event) {
    event?.stopPropagation();
    if (this.expandedHistoryIds.has(entry.accountId)) {
      this.expandedHistoryIds.delete(entry.accountId);
    } else {
      this.expandedHistoryIds.add(entry.accountId);
    }
  }

  toggleRecentInvoice(recent: RecentInvoiceCard) {
    this.selectRecentInvoice(recent);
    const key = recent.invoiceId || recent.invoiceNumber || '';
    if (!key) {
      return;
    }
    if (this.expandedRecentInvoiceIds.has(key)) {
      this.expandedRecentInvoiceIds.delete(key);
    } else {
      this.expandedRecentInvoiceIds.add(key);
    }
  }

  selectRecentInvoice(recent: RecentInvoiceCard) {
    const key = recent.invoiceId || recent.invoiceNumber || '';
    if (!key) {
      return;
    }
    this.selectedRecentKey = key;
  }

  isRecentInvoiceSelected(recent: RecentInvoiceCard): boolean {
    const key = recent.invoiceId || recent.invoiceNumber || '';
    return !!key && this.selectedRecentKey === key;
  }

  canCopySelectedRecent(): boolean {
    const selected = this.recentInvoices.find((invoice) => {
      const key = invoice.invoiceId || invoice.invoiceNumber || '';
      return !!key && key === this.selectedRecentKey;
    });
    return !!selected && selected.lines.length > 0;
  }

  copySelectedRecent() {
    let selected = this.recentInvoices.find((invoice) => {
      const key = invoice.invoiceId || invoice.invoiceNumber || '';
      return !!key && key === this.selectedRecentKey;
    });

    if (!selected && this.recentInvoices.length === 1) {
      selected = this.recentInvoices[0];
      this.selectRecentInvoice(selected);
    }

    if (!selected) {
      return;
    }

    this.codingCopied.emit({
      totalAmount: this.invoice?.amountDue ?? selected.amountDue,
      lines: selected.lines.map((line) => ({
        accountId: line.accountId,
        accountName: line.accountName,
        amountDue: line.amountDue,
      })),
    });
    this.closeCodingSuggestionsPanel();
  }

  isRecentInvoiceExpanded(recent: RecentInvoiceCard): boolean {
    const key = recent.invoiceId || recent.invoiceNumber || '';
    return !!key && this.expandedRecentInvoiceIds.has(key);
  }

  isHistoryExpanded(entry: AccountCodingHistoryEntry): boolean {
    return this.expandedHistoryIds.has(entry.accountId);
  }

  applySuggestedAccount() {
    const suggestion = this.codingSuggestions?.aiSuggestion;
    if (!suggestion) return;
    this.applySpecificSuggestion(suggestion);
  }

  applyAccountName(accountName: string) {
    if (!accountName) {
      return;
    }

    const selectedAccount = this.accounts?.find(
      (acc) => acc.name?.toLowerCase?.() === accountName.toLowerCase()
    );

    if (!selectedAccount) {
      this.toaster.displayToasterState(
        ToasterTypes.Warning,
        '',
        'Account is not available in your chart of accounts.'
      );
      return;
    }

    this.suggestionApplied.emit(selectedAccount);
    this.closeCodingSuggestionsPanel();
    this.toaster.displayToasterState(
      ToasterTypes.Success,
      '',
      'Account updated from previous coding.'
    );
  }

  private resetLocalState() {
    this.showPanel = false;
    this.codingSuggestionsLoading = false;
    this.codingSuggestionsError = '';
    this.codingSuggestions = null;
    this.expandedHistoryIds.clear();
    this.expandedRecentInvoiceIds.clear();
    this.selectedRecentKey = null;
    this.historyDetails = [];
    this.recentInvoices = [];
    this.expandedOtherKeys.clear();
    this.resetFeedbackState();
  }

  feedbackKey(suggestion: AccountCodingSuggestionDetail | null | undefined): string {
    if (!suggestion) return '';
    return suggestion.suggestionId
      || suggestion.accountId
      || suggestion.accountCode
      || suggestion.accountName
      || '';
  }

  toggleOtherSuggestion(suggestion: AccountCodingSuggestionDetail, event?: Event) {
    event?.stopPropagation();
    const key = this.feedbackKey(suggestion);
    if (!key) return;
    if (this.expandedOtherKeys.has(key)) {
      this.expandedOtherKeys.delete(key);
    } else {
      this.expandedOtherKeys.add(key);
    }
  }

  isOtherExpanded(suggestion: AccountCodingSuggestionDetail): boolean {
    const key = this.feedbackKey(suggestion);
    return !!key && this.expandedOtherKeys.has(key);
  }

  toggleFeedback(suggestion: AccountCodingSuggestionDetail | null | undefined, event?: Event) {
    event?.stopPropagation();
    const key = this.feedbackKey(suggestion);
    if (!key) return;
    if (this.feedbackOpenId === key) {
      this.resetFeedbackState();
      return;
    }
    this.feedbackOpenId = key;
    this.feedbackNote = '';
  }

  cancelFeedback(event?: Event) {
    event?.stopPropagation();
    this.resetFeedbackState();
  }

  submitFeedback(suggestion: AccountCodingSuggestionDetail | null | undefined, event?: Event) {
    event?.stopPropagation();
    if (!suggestion || this.feedbackSubmitting) return;

    const orgId =
      sessionStorage.organizationId
      || localStorage.getItem('SelectedOrganization')
      || localStorage.getItem('organizationId')
      || '';
    if (!orgId) {
      this.toaster.displayToasterState(ToasterTypes.Warning, '', 'Unable to send feedback: missing organisation.');
      return;
    }

    const resolvedSuggestedId = this.resolveSuggestionAccountId(suggestion);
    const chosenAccountId = this.invoice?.accountID || resolvedSuggestedId;
    if (!chosenAccountId) {
      this.toaster.displayToasterState(
        ToasterTypes.Warning,
        '',
        'Pick an account on this line before sending feedback.'
      );
      return;
    }

    const payload: RecordCodingFeedbackRequest = {
      orgId,
      vendorName: this.invoice?.supplierName || undefined,
      supplierContactId: this.invoice?.supplierId || undefined,
      invoiceId: this.invoice?.id?.toString() || undefined,
      lineDescription: this.invoice?.description || undefined,
      lineAmount: this.invoice?.amountDue ?? undefined,
      suggestedAccountId: resolvedSuggestedId || undefined,
      suggestedSource: suggestion.source || undefined,
      chosenAccountId,
      note: this.feedbackNote?.trim() || undefined,
    };

    this.feedbackSubmitting = true;
    this.invoiceService
      .recordCodingFeedback(payload)
      .pipe(take(1))
      .subscribe(
        () => {
          this.feedbackSubmitting = false;
          this.resetFeedbackState();
          this.toaster.displayToasterState(ToasterTypes.Success, '', 'Thanks — feedback recorded.');
        },
        (error: HttpErrorResponse) => {
          this.feedbackSubmitting = false;
          const message =
            (error?.error as { error?: string })?.error ||
            error?.message ||
            'Unable to send feedback right now.';
          this.toaster.displayToasterState(ToasterTypes.Error, '', message);
        }
      );
  }

  private resetFeedbackState() {
    this.feedbackOpenId = null;
    this.feedbackNote = '';
    this.feedbackSubmitting = false;
  }

}

interface AccountHistoryLineItem {
  invoiceId?: string;
  invoiceNumber?: string;
  invoiceDate?: string;
  accountId?: string;
  accountName?: string;
  amountDue?: number;
}

interface AccountHistoryDetail extends AccountCodingHistoryEntry {
  lines: AccountHistoryLineItem[];
}

interface RecentInvoiceCard {
  invoiceId?: string;
  invoiceNumber?: string;
  invoiceDate?: string;
  amountDue?: number;
  currency?: string;
  isMostRecent: boolean;
  summaryLabel: string;
  lines: Array<{
    accountId?: string;
    accountName: string;
    amountDue: number;
  }>;
}